import React, { FC, useEffect } from 'react';
import { Route, RouteProps, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Zendesk } from '@blocs.zendesk';
import { PermissionsProvider } from '@minecraft.permissions';
import { Loading } from '@minecraft.loader';
import { AuthLanguageSetter } from '../IntlFeature';
import { useUserContext } from '../UserContext';
import { ApplyCoupon } from './ApplyCoupon';
import { resetPaginatedCacheQueries } from './cacheHelper';

export interface AuthenticatedRouteProps extends RouteProps {
  redirectToRegisterPage?: boolean;
  shouldHideZendesk?: boolean;
}

const AuthenticatedRoute: FC<AuthenticatedRouteProps> = ({
  children,
  redirectToRegisterPage = false,
  shouldHideZendesk = false,
  ...restProps
}) => {
  const { isAuthenticated, isAuthenticating, loginError, verifyTokens } = useUserContext();
  const { cache } = useApolloClient();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectToRegistration = queryParams.get('redirectToRegistration');

  useEffect(() => {
    // this resets cached queries of lists of data
    // when we navigate to a new page
    // so that any mutations will be correctly
    // refetched and displayed if we return
    resetPaginatedCacheQueries({ cache });
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated && !isAuthenticating && !loginError) {
      verifyTokens({ redirectToTalentRegistration: redirectToRegisterPage || redirectToRegistration }).catch((e) => {
        console.error('verify tokens error', { error: e });
      });
    }
  }, [isAuthenticated, isAuthenticating, loginError, redirectToRegisterPage, redirectToRegistration]);

  if (loginError || !isAuthenticated) {
    return (
      <div
        data-testid="authenticated-route-loading-or-error"
        style={{ display: 'flex', alignItems: 'center', width: '80vw', height: '80vh', margin: '0 auto' }}
      >
        <Loading />
        &nbsp;
      </div>
    );
  }

  return (
    <Route {...restProps}>
      <PermissionsProvider>
        <Zendesk shouldHide={shouldHideZendesk} />
        <AuthLanguageSetter>
          <ApplyCoupon>{children}</ApplyCoupon>
        </AuthLanguageSetter>
      </PermissionsProvider>
    </Route>
  );
};

export default AuthenticatedRoute;
